/* eslint-disable no-param-reassign */
/* eslint-disable no-use-before-define */
/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
export type MarkmapLikeNode = {
  content: string;
  children?: MarkmapLikeNode[];
  payload?: Record<string, unknown>;
};

type InfoDirective = {
  anchorLine: number;
  markdown: string;
};

function extractInfoDirectives(markdown: string): InfoDirective[] {
  const directives: InfoDirective[] = [];
  const regex = /<!--\s*markmap:\s*info\b([\s\S]*?)-->/g;

  const matches = Array.from(markdown.matchAll(regex));
  for (const match of matches) {
    const body = (match[1] ?? '').trim();
    if (!body) {
      continue;
    }

    const matchIndex = match.index ?? 0;
    const before = markdown.slice(0, matchIndex);
    const lineParts = before.split(/\r?\n/);
    const currentLineIndex = lineParts.length - 1;
    const currentLinePrefix = lineParts[currentLineIndex] ?? '';
    let anchorLine = currentLinePrefix.trim() ? currentLineIndex + 1 : 0;

    if (anchorLine === 0) {
      for (let index = currentLineIndex - 1; index >= 0; index -= 1) {
        const candidate = (lineParts[index] ?? '').trim();
        if (!candidate || candidate.startsWith('<!--')) {
          continue;
        }
        anchorLine = index + 1;
        break;
      }
    }

    if (anchorLine > 0) {
      directives.push({
        anchorLine,
        markdown: body,
      });
    }
  }

  return directives;
}

function getNodeLineRange(
  node: MarkmapLikeNode
): { start: number; end: number } | null {
  const rawLines = node.payload?.lines;
  if (typeof rawLines !== 'string') {
    return null;
  }

  const [startRaw, endRaw] = rawLines
    .split(',')
    .map((value) => Number.parseInt(value.trim(), 10));
  if (!Number.isFinite(startRaw) || !Number.isFinite(endRaw)) {
    return null;
  }

  return {
    start: startRaw,
    end: endRaw,
  };
}

function findDeepestNodeForLine(
  root: MarkmapLikeNode,
  targetLine: number,
  nodeId: string
): { node: MarkmapLikeNode; nodeId: string } | null {
  for (let index = 0; index < (root.children?.length ?? 0); index += 1) {
    const child = root.children?.[index];
    if (!child) {
      continue;
    }
    const nested = findDeepestNodeForLine(
      child,
      targetLine,
      `${nodeId}.${index}`
    );
    if (nested) {
      return nested;
    }
  }

  const range = getNodeLineRange(root);
  if (!range || targetLine < range.start || targetLine > range.end) {
    return null;
  }

  return { node: root, nodeId };
}

export function enhanceMarkmapRoot(
  root: MarkmapLikeNode,
  markdown: string
): MarkmapLikeNode {
  const infoDirectives = extractInfoDirectives(markdown);
  if (infoDirectives.length === 0) {
    return root;
  }

  const infoByNodeId = new Map<string, string>();
  for (const directive of infoDirectives) {
    const node = findDeepestNodeForLine(root, directive.anchorLine, '0');
    if (!node) {
      continue;
    }
    const previous = infoByNodeId.get(node.nodeId);
    infoByNodeId.set(
      node.nodeId,
      previous ? `${previous}\n\n${directive.markdown}` : directive.markdown
    );
  }

  if (infoByNodeId.size === 0) {
    return root;
  }

  applyNodeInfo(root, '0', infoByNodeId);
  return root;
}

function applyNodeInfo(
  root: MarkmapLikeNode,
  nodeId: string,
  infoByNodeId: Map<string, string>
): void {
  const infoMarkdown = infoByNodeId.get(nodeId);
  if (infoMarkdown) {
    root.payload = {
      ...(root.payload ?? {}),
      info: infoMarkdown,
    };
  }

  root.children?.forEach((child, childIndex) => {
    applyNodeInfo(child, `${nodeId}.${childIndex}`, infoByNodeId);
  });
}
